import React from "react";
import { Card, CardTitle, Avatar, channelMeta, tagColors } from "./ui";
import { fmtBRL } from "../../CrmKanban/kanban/tokens";

const CHIP = {
  good: ["#E9F8EF", "#1E7A46"],
  warn: ["#FFF3DC", "#B7791F"],
  bad: ["#FDE8E8", "#C0392B"],
  neutral: ["#EEF1F4", "#5B6472"],
};

const respLevel = (min, meta) => {
  if (min <= meta) return "good";
  if (min <= meta * 2) return "warn";
  return "bad";
};

const Chip = ({ level, children }) => {
  const [bg, col] = CHIP[level] || CHIP.neutral;
  return (
    <span
      style={{
        background: bg,
        color: col,
        fontSize: 10,
        fontWeight: 800,
        padding: "1px 8px",
        borderRadius: 999,
      }}
    >
      {children}
    </span>
  );
};

const Row = ({ V, name, value, level }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <Avatar name={name} V={V} />
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 800,
        color: V.textStrong,
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {name}
    </span>
    <Chip level={level}>{value}</Chip>
  </div>
);

// Card dinâmico: Desempenho do atendente (filtrado) ou Leads por atendente.
const DynamicCard = ({ V, agFilterActive, desempenho, leadsPorAtendente }) => {
  if (agFilterActive && desempenho) {
    const line = (label, value, color) => (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          fontSize: 11.5,
          fontWeight: 700,
          color: V.sublabel,
        }}
      >
        <span style={{ flex: 1 }}>{label}</span>
        <span style={{ color: color || V.textStrong, fontWeight: 800 }}>
          {value}
        </span>
      </div>
    );
    return (
      <Card V={V} style={{ gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar name={desempenho.name} V={V} size={24} />
          <span
            style={{
              fontSize: 13.5,
              fontWeight: V.cardTitleWeight,
              color: V.textStrong,
            }}
          >
            {`Desempenho · ${desempenho.name}`}
          </span>
        </div>
        {line("Leads ativos", desempenho.leadsAtivos)}
        {line("Pipeline aberto", fmtBRL(desempenho.pipelineAberto), V.money)}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            fontSize: 11.5,
            fontWeight: 700,
            color: V.sublabel,
          }}
        >
          <span style={{ flex: 1 }}>1ª resposta</span>
          <Chip level={respLevel(desempenho.tprMin, 5)}>
            {`${desempenho.tprMin}min`}
          </Chip>
        </div>
        {line("Resolvidos no mês", desempenho.resolvidosMes)}
        {line("SLA em risco", desempenho.slaRisco, V.bad)}
      </Card>
    );
  }

  const max = Math.max(1, ...leadsPorAtendente.map((a) => a.count));
  const top = leadsPorAtendente[0];
  return (
    <Card V={V} style={{ gap: 9 }}>
      <CardTitle V={V}>Leads por atendente</CardTitle>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: V.sublabel }}>
        {top
          ? `${top.name} concentra ${top.count} leads ativos`
          : "Sem leads ativos"}
      </div>
      {leadsPorAtendente.map((a) => (
        <div
          key={a.userId}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <Avatar name={a.name} V={V} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                fontSize: 11.5,
                fontWeight: 800,
                color: V.textStrong,
              }}
            >
              <span style={{ flex: 1 }}>{a.name}</span>
              <span style={{ color: V.labelMuted }}>{a.count}</span>
            </div>
            <div
              style={{
                height: 5,
                borderRadius: 3,
                background: V.track,
                marginTop: 3,
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${(a.count / max) * 100}%`,
                  background: V.accent,
                  borderRadius: 3,
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </Card>
  );
};

// Donut de canais via conic-gradient acumulado.
const ChannelDonut = ({ V, canais, totalLeads }) => {
  let acc = 0;
  const stops = canais
    .map((c) => {
      const start = acc;
      acc += c.pct;
      const [, color] = channelMeta(c.channel);
      return `${color} ${start}% ${acc}%`;
    })
    .join(",");
  const gradient = canais.length
    ? `conic-gradient(${stops})`
    : `conic-gradient(${V.track} 0 100%)`;
  return (
    <Card V={V} style={{ gap: 12 }}>
      <CardTitle V={V}>Canais</CardTitle>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div
          style={{
            width: 104,
            height: 104,
            borderRadius: "50%",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: V.cardBg,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{ fontSize: 16, fontWeight: 900, color: V.textStrong }}
            >
              {totalLeads}
            </span>
            <span style={{ fontSize: 9, fontWeight: 800, color: V.labelMuted }}>
              LEADS
            </span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {canais.map((c) => {
            const [label, color] = channelMeta(c.channel);
            return (
              <span
                key={c.channel}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: V.sublabel,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: color,
                  }}
                />
                {`${label} · ${c.count} (${c.pct}%)`}
              </span>
            );
          })}
          {!canais.length && (
            <span style={{ fontSize: 11, color: V.labelMuted }}>Sem leads</span>
          )}
        </div>
      </div>
    </Card>
  );
};

// Linha 3 — 1ª resposta + card dinâmico + leads por tag + canais.
const Metrics = ({
  V,
  firstResponse,
  leadsPorAtendente,
  desempenho,
  leadsPorTag,
  canais,
  totalLeads,
  agFilterActive,
}) => {
  const meta = firstResponse.metaMin;
  const avg = firstResponse.avgMin;
  const acima = avg > meta;
  const maxTag = Math.max(1, ...leadsPorTag.map((t) => t.count));

  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}
    >
      <Card V={V} style={{ gap: 9 }}>
        <CardTitle V={V}>Tempo de 1ª resposta</CardTitle>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 26, fontWeight: 900, color: V.textStrong }}>
            {`${avg}min`}
          </span>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 800,
              color: acima ? V.bad : V.good,
            }}
          >
            {acima
              ? `acima da meta (${meta}min)`
              : `dentro da meta (${meta}min)`}
          </span>
        </div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: ".06em",
            color: V.labelMuted,
          }}
        >
          MAIS LENTOS PRIMEIRO
        </div>
        {firstResponse.ranking.length ? (
          firstResponse.ranking.map((r) => (
            <Row
              key={r.userId}
              V={V}
              name={r.name}
              value={`${r.min}min`}
              level={respLevel(r.min, meta)}
            />
          ))
        ) : (
          <span style={{ fontSize: 11, color: V.labelMuted }}>
            Sem respostas no período
          </span>
        )}
      </Card>

      <DynamicCard
        V={V}
        agFilterActive={agFilterActive}
        desempenho={desempenho}
        leadsPorAtendente={leadsPorAtendente}
      />

      <Card V={V} style={{ gap: 8 }}>
        <CardTitle V={V}>Leads por tag</CardTitle>
        {leadsPorTag.length ? (
          leadsPorTag.map((t) => {
            const [color] = tagColors(t.tag);
            return (
              <div
                key={t.tag}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: V.sublabel,
                    width: 70,
                    flex: "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.tag}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    background: V.track,
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${(t.count / maxTag) * 100}%`,
                      background: color,
                      borderRadius: 3,
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 900,
                    color: V.textStrong,
                    width: 14,
                    textAlign: "right",
                  }}
                >
                  {t.count}
                </span>
              </div>
            );
          })
        ) : (
          <span style={{ fontSize: 11, color: V.labelMuted }}>Sem tags</span>
        )}
      </Card>

      <ChannelDonut V={V} canais={canais} totalLeads={totalLeads} />
    </div>
  );
};

export default Metrics;
